"use client";
import Image from "next/image";
import React, { useRef, useEffect, useState, useCallback } from "react";

interface Tab {
  id: string;
  label: string;
}

interface ExpertOpinionTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

const ChevronLeft: React.FC = () => (
  <Image
    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
    alt="view-all-icon"
    title="view-all-icon"
    width={16}
    height={16}
    className="rotate-180"
  />
);

const ChevronRight: React.FC = () => (
  <Image
    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
    alt="view-all-icon"
    title="view-all-icon"
    width={16}
    height={16}
  />
);

const ExpertOpinionTabs: React.FC<ExpertOpinionTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLHeadingElement | null>>({});
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll, tabs.length]);

  const stopScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    window.removeEventListener("mouseup", stopScroll);
    window.removeEventListener("touchend", stopScroll);
    window.removeEventListener("pointerup", stopScroll);
  }, []);

  const startScroll = useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;

      stopScroll();
      const step = direction === "right" ? 8 : -8;

      intervalRef.current = setInterval(() => {
        el.scrollLeft += step;
        if (
          (direction === "left" && el.scrollLeft <= 0) ||
          (direction === "right" &&
            el.scrollLeft + el.clientWidth >= el.scrollWidth - 1)
        ) {
          stopScroll();
        }
      }, 16);

      window.addEventListener("mouseup", stopScroll);
      window.addEventListener("touchend", stopScroll);
      window.addEventListener("pointerup", stopScroll);
    },
    [stopScroll],
  );

  useEffect(() => {
    const container = scrollRef.current;
    const activeEl = tabRefs.current[activeTab];

    if (container && activeEl) {
      const containerWidth = container.offsetWidth;
      const tabWidth = activeEl.offsetWidth;
      const tabOffsetLeft = activeEl.offsetLeft;

      const scrollTo = tabOffsetLeft - containerWidth / 2 + tabWidth / 2;

      container.scrollTo({
        left: scrollTo,
        behavior: "smooth",
      });

      const timer = setTimeout(checkScroll, 350);
      return () => clearTimeout(timer);
    }
  }, [activeTab, checkScroll]);

  return (
    <div className="relative flex items-center -ml-[12px] -mr-[12px] md:-ml-[20px] md:-mr-[20px] px-3 md:px-0">
      <div className="hidden md:flex w-8 flex-shrink-0 justify-center">
        {canScrollLeft && (
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-black z-10"
            onMouseDown={() => startScroll("left")}
            onTouchStart={() => startScroll("left")}
          >
            <ChevronLeft />
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto flex-1 min-w-0 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-1"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <h3
              key={tab.id}
              ref={(node) => {
                tabRefs.current[tab.id] = node;
              }}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex-shrink-0 p-3 md:p-4 rounded-[8px] text-[14px] font-medium
                whitespace-nowrap transition-all duration-150
                border-[1.5px] leading-snug cursor-pointer
                min-w-[150px] text-center
                ${
                  isActive
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-gray-300"
                }
              `}
            >
              {tab.label}
            </h3>
          );
        })}
      </div>

      <div className="hidden md:flex w-8 flex-shrink-0 justify-center">
        {canScrollRight && (
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-black z-10"
            onMouseDown={() => startScroll("right")}
            onTouchStart={() => startScroll("right")}
          >
            <ChevronRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpertOpinionTabs;